<script setup>
import { Comment, Fragment, Text, computed, onBeforeUnmount, onMounted, onUpdated, ref, shallowRef, useSlots, watch } from 'vue'
import Swiper, { Autoplay } from 'swiper'
import 'swiper/css'

// Swiper ignores the finger until it has moved this far — and leaves the touch
// to the page meanwhile, so iOS Safari takes a slightly diagonal swipe as a
// scroll. Kept just big enough that a shaky tap on a slide is still a click.
const TOUCH_THRESHOLD = 8

const RECREATE_PROPS = [
    'perPage',
    'perPageCustom',
    'scrollPerPage',
    'autoplay',
    'loop',
    'autoplayTimeout',
    'spacePadding',
    'mouseDrag',
    'resistanceCoef',
]

const props = defineProps({
    perPage: {
        type: Number,
        default: 1,
    },
    perPageCustom: {
        type: Array,
        default: () => [],
    },
    paginationEnabled: {
        type: Boolean,
        default: true,
    },
    resistanceCoef: {
        type: Number,
        default: 20,
    },
    scrollPerPage: {
        type: Boolean,
        default: true,
    },
    autoplay: {
        type: Boolean,
        default: false,
    },
    loop: {
        type: Boolean,
        default: false,
    },
    autoplayTimeout: {
        type: Number,
        default: 5000,
    },
    spacePadding: {
        type: Number,
        default: 0,
    },
    mouseDrag: {
        type: Boolean,
        default: true,
    },
})
const emit = defineEmits(['state-change'])
const slots = useSlots()

const container = ref(null)
const swiper = shallowRef(null)
const slideCount = ref(0)
const currentPage = ref(0)
const pageCount = ref(0)
const createFrame = ref(null)
const resizeObserver = shallowRef(null)
const lastContainerWidth = ref(0)

const normalizedPerPage = computed(() => Number(props.perPage) || 1)
const breakpointConfig = computed(() => {
    if (!props.perPageCustom.length) return undefined

    return props.perPageCustom.reduce((config, [width, slidesPerView]) => {
        const normalizedSlides = Number(slidesPerView) || 1
        config[Number(width) || 0] = {
            slidesPerView: normalizedSlides,
            slidesPerGroup: props.scrollPerPage ? normalizedSlides : 1,
        }
        return config
    }, {})
})
const paginationPageCount = computed(() => props.loop ? slideCount.value : pageCount.value)

function getResistanceRatio() {
    if (props.resistanceCoef <= 0) return 0
    return Math.max(0.15, Math.min(0.85, 20 / props.resistanceCoef))
}

// A `v-for` in the slot arrives as a single Fragment, so the real slides are
// one level down.
function countSlides(vnodes) {
    return vnodes.reduce((count, vnode) => {
        if (!vnode || vnode.type === Comment) return count
        if (vnode.type === Fragment) return count + countSlides(vnode.children || [])
        if (vnode.type === Text) return count + (String(vnode.children).trim().length > 0 ? 1 : 0)
        return count + 1
    }, 0)
}

function getSlideCount() {
    return countSlides(slots.default?.() || [])
}

function buildOptions(initialPage = 0) {
    const maxPage = Math.max(slideCount.value - 1, 0)
    const initialSlide = Math.max(0, Math.min(initialPage, maxPage))

    return {
        modules: [Autoplay],
        watchOverflow: true,
        updateOnWindowResize: true,
        loop: props.loop && slideCount.value > 1,
        initialSlide,
        autoplay: props.autoplay
            ? { delay: props.autoplayTimeout, disableOnInteraction: false }
            : false,
        slidesPerView: normalizedPerPage.value,
        slidesPerGroup: props.scrollPerPage ? normalizedPerPage.value : 1,
        breakpoints: breakpointConfig.value,
        threshold: TOUCH_THRESHOLD,
        resistance: props.resistanceCoef > 0,
        resistanceRatio: getResistanceRatio(),
        simulateTouch: props.mouseDrag,
        allowTouchMove: props.mouseDrag && slideCount.value > 1,
        slidesOffsetBefore: props.spacePadding,
        slidesOffsetAfter: props.spacePadding,
    }
}

function emitState() {
    emit('state-change', {
        currentPage: currentPage.value,
        pageCount: pageCount.value,
        isBeginning: currentPage.value === 0,
        isEnd: pageCount.value <= 1 || currentPage.value >= pageCount.value - 1,
    })
}

function syncState() {
    if (!swiper.value) {
        currentPage.value = 0
        pageCount.value = 0
        emitState()
        return
    }

    slideCount.value = getSlideCount()
    currentPage.value = swiper.value.params.loop ? swiper.value.realIndex : swiper.value.activeIndex
    pageCount.value = swiper.value.params.loop ? slideCount.value : swiper.value.snapGrid.length
    emitState()
}

function handleSlideChange() {
    syncState()
}

function cancelCreate() {
    if (!createFrame.value) return
    window.cancelAnimationFrame(createFrame.value)
    createFrame.value = null
}

function createSwiper(initialPage = 0) {
    if (!container.value || swiper.value) return

    slideCount.value = getSlideCount()
    if (slideCount.value === 0) {
        emitState()
        return
    }

    const width = container.value.clientWidth
    if (width === 0) {
        scheduleCreate(initialPage)
        return
    }

    lastContainerWidth.value = width
    swiper.value = new Swiper(container.value, buildOptions(initialPage))
    swiper.value.on('slideChange', handleSlideChange)
    syncState()
}

function scheduleCreate(initialPage = currentPage.value) {
    cancelCreate()
    createFrame.value = window.requestAnimationFrame(() => {
        createFrame.value = null
        createSwiper(initialPage)
    })
}

function destroySwiper() {
    if (!swiper.value) return
    swiper.value.off('slideChange', handleSlideChange)
    swiper.value.destroy(true, false)
    swiper.value = null
}

function recreate(initialPage = currentPage.value) {
    destroySwiper()
    scheduleCreate(initialPage)
}

function syncSlides() {
    const nextSlideCount = getSlideCount()
    if (nextSlideCount === slideCount.value) return

    slideCount.value = nextSlideCount
    if (nextSlideCount === 0) {
        destroySwiper()
        currentPage.value = 0
        pageCount.value = 0
        emitState()
        return
    }
    recreate()
}

function startResizeObserver() {
    if (typeof ResizeObserver === 'undefined' || !container.value) return

    resizeObserver.value = new ResizeObserver(([entry]) => {
        const width = entry?.contentRect?.width || 0
        if (width === 0 || width === lastContainerWidth.value) return

        lastContainerWidth.value = width
        if (!swiper.value) {
            scheduleCreate()
            return
        }
        recreate()
    })
    resizeObserver.value.observe(container.value)
}

function stopResizeObserver() {
    if (!resizeObserver.value) return
    resizeObserver.value.disconnect()
    resizeObserver.value = null
}

function next() {
    if (!swiper.value || swiper.value.destroyed) return
    swiper.value.slideNext()
}

function prev() {
    if (!swiper.value || swiper.value.destroyed) return
    swiper.value.slidePrev()
}

function goToPage(page) {
    if (!swiper.value || swiper.value.destroyed) return
    if (swiper.value.params.loop) swiper.value.slideToLoop(page)
    else swiper.value.slideTo(page)
}

RECREATE_PROPS.forEach(key => {
    watch(() => props[key], () => recreate(), { deep: key === 'perPageCustom' })
})

onMounted(() => {
    startResizeObserver()
    scheduleCreate()
})
onUpdated(syncSlides)
onBeforeUnmount(() => {
    stopResizeObserver()
    cancelCreate()
    destroySwiper()
})

defineExpose({ next, prev, goToPage })
</script>

<template>
    <div
        v-bind="$attrs"
        class="shared-carousel"
    >
        <div
            ref="container"
            class="shared-carousel-viewport swiper"
        >
            <div
                ref="wrapper"
                class="swiper-wrapper"
            >
                <slot />
            </div>
        </div>

        <div
            v-if="paginationEnabled && paginationPageCount > 1"
            class="shared-carousel-pagination"
        >
            <div class="shared-carousel-dots">
                <button
                    v-for="page in paginationPageCount"
                    :key="`shared-carousel-dot-${page - 1}`"
                    type="button"
                    :class="[
                        'shared-carousel-dot',
                        { 'is-active': currentPage === page - 1 },
                    ]"
                    :aria-label="`Go to slide ${page}`"
                    @click="goToPage(page - 1)"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.shared-carousel {
    position: relative;
}

.shared-carousel-viewport {
    overflow: hidden;
}

.shared-carousel-slide {
    height: auto;
}

.shared-carousel-dots {
    display: inline-flex;
    align-items: center;
}

.shared-carousel-dot {
    display: block;
    appearance: none;
    cursor: pointer;
    background: transparent;
    border: 0;
}
</style>
